import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, CircularProgress, TextField, Grid,
  FormControl, InputLabel, Select, MenuItem, Card, CardContent
} from "@mui/material";
import { Visibility, Cancel, Search, Refresh, Flight, Download, CheckCircle } from "@mui/icons-material";
import axios from "axios";
import { api_urls } from "../../../utils/api-urls";

const FlightBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchBookings();
    fetchStats();
  }, [page, rowsPerPage, statusFilter]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
      });
      if (statusFilter) params.append("status", statusFilter);
      if (searchText) params.append("search", searchText);

      const response = await axios.get(`${api_urls}api/tbo/admin/flight-bookings?${params}`);
      if (response.data?.success) {
        setBookings(response.data.data || []);
        setTotalCount(response.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching flight bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${api_urls}api/tbo/admin/booking-stats`);
      if (response.data?.success) {
        setStats(response.data.flight);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchBookings();
  };

  const handleViewDetails = (booking) => {
    setSelectedBooking(booking);
    setDetailsOpen(true);
  };

  const handleCancelBooking = async (booking) => {
    if (window.confirm("Are you sure you want to cancel this booking?")) {
      try {
        const response = await axios.post(`${api_urls}api/tbo/flight/cancel`, {
          bookingId: booking.bookingId || booking._id,
          pnr: booking.pnr
        });
        if (response.data?.success) {
          alert("Cancellation request submitted successfully");
          fetchBookings();
          fetchStats();
        } else {
          alert("Cancellation failed: " + (response.data?.message || "Unknown error"));
        }
      } catch (error) {
        console.error("Cancel error:", error);
        alert("Error cancelling booking");
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "booking_success": case "confirmed": case "ticketed": return "success";
      case "started": case "payment_initiated": case "pending": return "warning";
      case "payment_cancelled": return "info";
      case "cancelled": case "payment_failed": case "booking_failed": return "error";
      case "cancellation_requested": return "warning";
      default: return "default";
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      started: "Started",
      payment_initiated: "Payment Initiated",
      payment_cancelled: "Payment Cancelled",
      payment_failed: "Payment Failed",
      booking_success: "Booked",
      booking_failed: "Booking Failed",
      confirmed: "Confirmed",
      ticketed: "Ticketed",
      cancelled: "Cancelled",
      cancellation_requested: "Cancel Requested"
    };
    return labels[status] || status || "Unknown";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric"
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  // Helper to get user name - handles both firstName and firstname
  const getUserName = (user) => {
    if (!user) return "-";
    const first = user.firstName || user.firstname || "";
    const last = user.lastName || user.lastname || "";
    return (first + " " + last).trim() || "-";
  };

  // Helper to get user phone - handles both phone and mobile
  const getUserPhone = (user) => {
    if (!user) return "-";
    return user.phone || user.mobile || "-";
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
        <Flight sx={{ mr: 1, verticalAlign: "bottom" }} />
        Flight Bookings
      </Typography>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#e3f2fd" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold">{stats.total || 0}</Typography>
                <Typography variant="caption">Total</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#e8f5e9" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" color="success.main">{stats.confirmed || 0}</Typography>
                <Typography variant="caption">Confirmed</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#fff8e1" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" color="warning.main">{stats.cancellation_requested || 0}</Typography>
                <Typography variant="caption">Cancel Requested</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#fff3e0" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" color="warning.main">{stats.started || 0}</Typography>
                <Typography variant="caption">In Progress</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#e0f7fa" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" color="info.main">{stats.payment_cancelled || 0}</Typography>
                <Typography variant="caption">Payment Cancelled</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#ffebee" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" color="error.main">{(stats.payment_failed || 0) + (stats.booking_failed || 0)}</Typography>
                <Typography variant="caption">Failed</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#c8e6c9" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" color="success.main">₹{(stats.totalConfirmedAmount || 0).toLocaleString()}</Typography>
                <Typography variant="caption">Confirmed Amount</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#bbdefb" }}>
              <CardContent sx={{ py: 1, textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" color="primary.main">₹{(stats.adminCommission || 0).toLocaleString()}</Typography>
                <Typography variant="caption">Admin Commission (2%)</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Box mb={3} display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Search PNR, Route, Airline..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          InputProps={{
            startAdornment: <Search color="action" sx={{ mr: 1 }} />
          }}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="confirmed">Confirmed</MenuItem>
            <MenuItem value="started">Started</MenuItem>
            <MenuItem value="payment_initiated">Payment Initiated</MenuItem>
            <MenuItem value="payment_cancelled">Payment Cancelled</MenuItem>
            <MenuItem value="payment_failed">Payment Failed</MenuItem>
            <MenuItem value="booking_success">Booked</MenuItem>
            <MenuItem value="booking_failed">Booking Failed</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleSearch}>Search</Button>
        <Button variant="outlined" startIcon={<Refresh />} onClick={() => { setSearchText(""); setStatusFilter(""); fetchBookings(); fetchStats(); }}>
          Reset
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#1976d2" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>PNR/ID</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>User</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Route</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Airline</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Date</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Amount</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary" py={3}>No flight bookings found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((booking, index) => (
                  <TableRow key={booking._id || index} hover>
                    <TableCell>
                      <Typography fontWeight="bold">{booking.pnr || booking.bookingId || booking._id?.slice(-8) || "-"}</Typography>
                      {booking.bookingId && <Typography variant="caption" color="textSecondary">ID: {booking.bookingId}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{getUserName(booking.userId)}</Typography>
                      <Typography variant="caption" color="textSecondary">{getUserPhone(booking.userId)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="medium">{booking.origin} → {booking.destination}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{booking.airlineName || "-"}</Typography>
                      <Typography variant="caption" color="textSecondary">{booking.flightNumber ? `${booking.airlineCode || ""}${booking.flightNumber}` : "-"}</Typography>
                    </TableCell>
                    <TableCell>{formatDate(booking.departureDate)}</TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">₹{booking.amount?.toLocaleString() || "-"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(booking.status)}
                        color={getStatusColor(booking.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleViewDetails(booking)}
                        title="View Details"
                        size="small"
                      >
                        <Visibility />
                      </IconButton>
                      {(booking.status === "booking_success" || booking.status === "confirmed" || booking.status === "ticketed" || booking.type === "confirmed_booking") && (
                        <IconButton
                          color="success"
                          onClick={() => window.open(`${api_urls}api/tbo/admin/flight-ticket/${booking.bookingId || booking._id}`, "_blank")}
                          title="Download Ticket"
                          size="small"
                        >
                          <Download />
                        </IconButton>
                      )}
                      {(booking.status === "confirmed" || booking.status === "ticketed") && (
                        <IconButton
                          color="error"
                          onClick={() => handleCancelBooking(booking)}
                          title="Request Cancellation"
                          size="small"
                        >
                          <Cancel />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}

      {/* Booking Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: "#1976d2", color: "white" }}>
          <Typography variant="h6" fontWeight="bold">
            <Flight sx={{ mr: 1, verticalAlign: "bottom" }} />
            Flight Booking Details
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedBooking && (
            <Grid container spacing={2}>
              {/* Booking Info */}
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">PNR</Typography>
                <Typography fontWeight="bold" fontSize="1.2rem">{selectedBooking.pnr || "Pending"}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Booking ID</Typography>
                <Typography fontWeight="bold">{selectedBooking.bookingId || "-"}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Status</Typography>
                <Box>
                  <Chip label={getStatusLabel(selectedBooking.status)} color={getStatusColor(selectedBooking.status)} />
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Amount</Typography>
                <Typography fontWeight="bold" fontSize="1.2rem">₹{selectedBooking.amount?.toLocaleString()}</Typography>
              </Grid>

              <Grid item xs={12}><hr /></Grid>

              {/* Flight Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" fontWeight="bold">✈️ Flight Details</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Route</Typography>
                <Typography fontWeight="bold">{selectedBooking.origin} → {selectedBooking.destination}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Airline</Typography>
                <Typography>{selectedBooking.airlineName || "-"}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Flight Number</Typography>
                <Typography>{selectedBooking.flightNumber ? `${selectedBooking.airlineCode || ""}${selectedBooking.flightNumber}` : "-"}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Class</Typography>
                <Typography>{selectedBooking.cabinClass || "-"}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Departure</Typography>
                <Typography>{formatDateTime(selectedBooking.departureDate)}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Return</Typography>
                <Typography>{selectedBooking.returnDate ? formatDate(selectedBooking.returnDate) : "-"}</Typography>
              </Grid>

              {/* Segments */}
              {selectedBooking.segments?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Flight Segments</Typography>
                  <Box sx={{ bgcolor: "#f5f5f5", p: 1, borderRadius: 1, mt: 0.5 }}>
                    {selectedBooking.segments.map((seg, idx) => (
                      <Typography key={idx} variant="body2">
                        {idx + 1}. {seg.origin} → {seg.destination} | {seg.airlineName} {seg.flightNumber} | Dep: {formatDateTime(seg.departureTime)} | Arr: {formatDateTime(seg.arrivalTime)}
                      </Typography>
                    ))}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}><hr /></Grid>

              {/* User Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" fontWeight="bold">👤 Customer Details</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="textSecondary">Name</Typography>
                <Typography>{getUserName(selectedBooking.userId)}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="textSecondary">Mobile</Typography>
                <Typography>{getUserPhone(selectedBooking.userId)}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="textSecondary">Email</Typography>
                <Typography>{selectedBooking.userId?.email || selectedBooking.contactEmail || "-"}</Typography>
              </Grid>
              {(selectedBooking.contactPhone || selectedBooking.contactEmail) && (
                <>
                  <Grid item xs={6} md={4}>
                    <Typography variant="caption" color="textSecondary">Contact Phone</Typography>
                    <Typography>{selectedBooking.contactPhone || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Typography variant="caption" color="textSecondary">Contact Email</Typography>
                    <Typography>{selectedBooking.contactEmail || "-"}</Typography>
                  </Grid>
                </>
              )}

              <Grid item xs={12}><hr /></Grid>

              {/* Passengers */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" fontWeight="bold">🎫 Passengers ({selectedBooking.passengerCount || selectedBooking.passengers?.length || 0})</Typography>
              </Grid>
              {selectedBooking.passengers?.length > 0 ? (
                <Grid item xs={12}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                          <TableCell>#</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Ticket Number</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedBooking.passengers.map((p, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{p.title} {p.firstName} {p.lastName}</TableCell>
                            <TableCell>{p.type === 1 ? "Adult" : p.type === 2 ? "Child" : p.type === 3 ? "Infant" : p.type}</TableCell>
                            <TableCell>{p.ticketNumber || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Typography color="textSecondary">{selectedBooking.passengerName || "No passenger details"}</Typography>
                </Grid>
              )}

              {/* Payment Info */}
              {(selectedBooking.razorpayOrderId || selectedBooking.razorpayPaymentId) && (
                <>
                  <Grid item xs={12}><hr /></Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary" fontWeight="bold">💳 Payment Details</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Razorpay Order ID</Typography>
                    <Typography variant="body2">{selectedBooking.razorpayOrderId || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Razorpay Payment ID</Typography>
                    <Typography variant="body2">{selectedBooking.razorpayPaymentId || "-"}</Typography>
                  </Grid>
                </>
              )}

              {/* Error Message */}
              {selectedBooking.errorMessage && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Error Message</Typography>
                  <Typography color="error" sx={{ bgcolor: "#ffebee", p: 1, borderRadius: 1 }}>{selectedBooking.errorMessage}</Typography>
                </Grid>
              )}

              {/* Timestamps */}
              <Grid item xs={12}><hr /></Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Created</Typography>
                <Typography variant="body2">{formatDateTime(selectedBooking.createdAt)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Type</Typography>
                <Typography variant="body2">{selectedBooking.type || "-"}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {(selectedBooking?.status === "confirmed" || selectedBooking?.status === "ticketed" || selectedBooking?.type === "confirmed_booking") && (
            <Button
              startIcon={<Download />}
              variant="outlined"
              color="primary"
              onClick={() => window.open(`${api_urls}api/tbo/admin/flight-ticket/${selectedBooking?.bookingId || selectedBooking?._id}`, "_blank")}
            >
              Download Ticket
            </Button>
          )}
          <Button onClick={() => setDetailsOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FlightBookings;
